import { useAppDispatch } from "@/hooks";
import {
    fetchClientOrganizations,
    selectClientOrganizationLoading,
    selectClientOrganizations,
    selectSelectedOrganization,
    setSelectedOrganization,
} from "@/store/slices/clientOrganizationSlice";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const OrganizationDropdown: React.FC = () => {
    const dispatch = useAppDispatch();
    const organizations = useSelector(selectClientOrganizations);
    const selectedOrganization = useSelector(selectSelectedOrganization);
    const loading = useSelector(selectClientOrganizationLoading);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch organizations on mount
    useEffect(() => {
        dispatch(fetchClientOrganizations());
    }, [dispatch]);

    // Handle click outside to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSelect = (org: (typeof organizations)[0]) => {
        dispatch(setSelectedOrganization(org));
        setIsOpen(false);
    };

    if (loading && organizations.length === 0) {
        return (
            <div className="flex items-center px-3 py-2 text-sm text-gray-500 mr-4">
                Loading...
            </div>
        );
    }

    if (organizations.length === 0) {
        return null;
    }

    return (
        <div className="relative mr-4" ref={dropdownRef}>
            <div
                className={`w-[180px] h-[42px] px-3 border border-[#E0E0E0] rounded-[6px] flex items-center justify-between cursor-pointer bg-white`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span
                    className={`text-[14px] truncate ${selectedOrganization ? "text-[#1E1E1E]" : "text-[#B3B3B3]"
                        }`}
                >
                    {selectedOrganization?.org_name || "Select organization"}
                </span>
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                    ></path>
                </svg>
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-1">
                        {organizations.map((org) => (
                            <div
                                key={org.client_id}
                                className={`p-2 hover:bg-[#F9FEFF] cursor-pointer rounded text-[14px] ${selectedOrganization?.client_id === org.client_id
                                        ? "bg-[#F9FEFF] text-[#73B5C5] font-[500]"
                                        : "text-[#1E1E1E]"
                                    }`}
                                onClick={() => handleSelect(org)}
                            >
                                {org.org_name}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationDropdown;
